import type {
	CreateParams,
	CreateResult,
	DataProvider,
	DeleteManyParams,
	DeleteManyResult,
	DeleteParams,
	DeleteResult,
	GetListParams,
	GetListResult,
	GetManyParams,
	GetManyReferenceParams,
	GetManyReferenceResult,
	GetManyResult,
	GetOneParams,
	GetOneResult,
	Identifier,
	UpdateManyParams,
	UpdateManyResult,
	UpdateParams,
	UpdateResult,
} from "react-admin";
import { ADMIN_API_URL, API_URL } from "@/config/api";

// Custom HTTP client with credentials for cookie-based auth
const httpClient = async (
	url: string,
	options: RequestInit = {},
): Promise<{ json: unknown; status: number; headers: Headers }> => {
	const response = await fetch(url, {
		...options,
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
			...options.headers,
		},
	});

	let json: unknown;
	try {
		json = await response.json();
	} catch {
		json = {};
	}

	if (!response.ok) {
		const error = json as { message?: string };
		throw new Error(error.message || `HTTP Error ${response.status}`);
	}

	return { json, status: response.status, headers: response.headers };
};

// Build query string from React Admin params
const buildQueryString = (params: GetListParams): string => {
	const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
	const { field, order } = params.sort ?? { field: "id", order: "ASC" };
	const filter = (params.filter ?? {}) as Record<string, unknown>;

	const query = new URLSearchParams();
	query.set("page", String(page));
	query.set("perPage", String(perPage));
	query.set("sortField", field);
	query.set("sortOrder", order);

	// Handle filters
	for (const [key, value] of Object.entries(filter)) {
		if (value !== undefined && value !== null && value !== "") {
			if (key === "includeDeleted") {
				query.set("includeDeleted", String(value));
			} else {
				query.set(`filter[${key}]`, String(value));
			}
		}
	}

	return query.toString();
};

// Extended data provider with custom methods
export interface AdminDataProvider extends DataProvider {
	restore: (resource: string, id: Identifier) => Promise<{ data: unknown }>;
	ban: (
		id: Identifier,
		reason?: string,
		expires?: Date,
	) => Promise<{ data: unknown }>;
	unban: (id: Identifier) => Promise<{ data: unknown }>;
	updateRole: (
		id: Identifier,
		role: "user" | "admin" | "super_admin",
	) => Promise<{ data: unknown }>;
	impersonateUser: (id: Identifier) => Promise<{ data: unknown }>;
}

export const dataProvider: AdminDataProvider = {
	getList: async <RecordType extends { id: Identifier }>(
		resource: string,
		params: GetListParams,
	): Promise<GetListResult<RecordType>> => {
		const query = buildQueryString(params);
		const url = `${ADMIN_API_URL}/${resource}?${query}`;

		const { json } = await httpClient(url);
		const response = json as { data: RecordType[]; total: number };

		return {
			data: response.data,
			total: response.total,
		};
	},

	getOne: async <RecordType extends { id: Identifier }>(
		resource: string,
		params: GetOneParams,
	): Promise<GetOneResult<RecordType>> => {
		const url = `${ADMIN_API_URL}/${resource}/${params.id}`;

		const { json } = await httpClient(url);
		const response = json as { data: RecordType };

		return { data: response.data };
	},

	getMany: async <RecordType extends { id: Identifier }>(
		resource: string,
		params: GetManyParams,
	): Promise<GetManyResult<RecordType>> => {
		const query = new URLSearchParams();
		params.ids.forEach((id) => {
			query.append("ids", String(id));
		});

		const url = `${ADMIN_API_URL}/${resource}/batch?${query.toString()}`;

		const { json } = await httpClient(url);
		const response = json as { data: RecordType[] };

		return { data: response.data };
	},

	getManyReference: async <RecordType extends { id: Identifier }>(
		resource: string,
		params: GetManyReferenceParams,
	): Promise<GetManyReferenceResult<RecordType>> => {
		const { page, perPage } = params.pagination;
		const { field, order } = params.sort;
		const filter = params.filter as Record<string, unknown>;

		const query = new URLSearchParams();
		query.set("page", String(page));
		query.set("perPage", String(perPage));
		query.set("sortField", field);
		query.set("sortOrder", order);
		query.set(`filter[${params.target}]`, String(params.id));

		for (const [key, value] of Object.entries(filter)) {
			if (value !== undefined && value !== null && value !== "") {
				query.set(`filter[${key}]`, String(value));
			}
		}

		const url = `${ADMIN_API_URL}/${resource}?${query.toString()}`;

		const { json } = await httpClient(url);
		const response = json as { data: RecordType[]; total: number };

		return {
			data: response.data,
			total: response.total,
		};
	},

	create: async <RecordType extends { id: Identifier }>(
		resource: string,
		params: CreateParams,
	): Promise<CreateResult<RecordType>> => {
		const url = `${ADMIN_API_URL}/${resource}`;

		const { json } = await httpClient(url, {
			method: "POST",
			body: JSON.stringify(params.data),
		});
		const response = json as { data: RecordType };

		return { data: response.data };
	},

	update: async <RecordType extends { id: Identifier }>(
		resource: string,
		params: UpdateParams,
	): Promise<UpdateResult<RecordType>> => {
		const url = `${ADMIN_API_URL}/${resource}/${params.id}`;

		const { json } = await httpClient(url, {
			method: "PUT",
			body: JSON.stringify(params.data),
		});
		const response = json as { data: RecordType };

		return { data: response.data };
	},

	updateMany: async (
		resource: string,
		params: UpdateManyParams,
	): Promise<UpdateManyResult> => {
		// Update each record individually
		const results = await Promise.all(
			params.ids.map((id) =>
				httpClient(`${ADMIN_API_URL}/${resource}/${id}`, {
					method: "PUT",
					body: JSON.stringify(params.data),
				}),
			),
		);

		return { data: results.map((_, index) => params.ids[index]) };
	},

	delete: async <RecordType extends { id: Identifier }>(
		resource: string,
		params: DeleteParams,
	): Promise<DeleteResult<RecordType>> => {
		const url = `${ADMIN_API_URL}/${resource}/${params.id}`;

		await httpClient(url, { method: "DELETE" });

		return { data: params.previousData as RecordType };
	},

	deleteMany: async (
		resource: string,
		params: DeleteManyParams,
	): Promise<DeleteManyResult> => {
		const query = new URLSearchParams();
		params.ids.forEach((id) => {
			query.append("ids", String(id));
		});

		const url = `${ADMIN_API_URL}/${resource}/batch?${query.toString()}`;

		await httpClient(url, { method: "DELETE" });

		return { data: params.ids };
	},

	// Custom method: Restore soft-deleted record
	restore: async (
		resource: string,
		id: Identifier,
	): Promise<{ data: unknown }> => {
		const url = `${ADMIN_API_URL}/${resource}/${id}/restore`;

		const { json } = await httpClient(url, { method: "POST" });
		return { data: (json as { data: unknown }).data };
	},

	// Custom method: Ban user
	ban: async (
		id: Identifier,
		reason?: string,
		expires?: Date,
	): Promise<{ data: unknown }> => {
		const url = `${ADMIN_API_URL}/users/${id}/ban`;

		const { json } = await httpClient(url, {
			method: "POST",
			body: JSON.stringify({
				reason,
				expires: expires?.toISOString(),
			}),
		});
		return { data: (json as { data: unknown }).data };
	},

	// Custom method: Unban user
	unban: async (id: Identifier): Promise<{ data: unknown }> => {
		const url = `${ADMIN_API_URL}/users/${id}/unban`;

		const { json } = await httpClient(url, { method: "POST" });
		return { data: (json as { data: unknown }).data };
	},

	// Custom method: Update user role
	updateRole: async (
		id: Identifier,
		role: "user" | "admin" | "super_admin",
	): Promise<{ data: unknown }> => {
		const url = `${ADMIN_API_URL}/users/${id}/role`;

		const { json } = await httpClient(url, {
			method: "PUT",
			body: JSON.stringify({ role }),
		});
		return { data: (json as { data: unknown }).data };
	},

	// Custom method: Impersonate user (calls better-auth directly for cookie handling)
	impersonateUser: async (id: Identifier): Promise<{ data: unknown }> => {
		const url = `${API_URL}/api/auth/admin/impersonate-user`;

		const { json } = await httpClient(url, {
			method: "POST",
			body: JSON.stringify({ userId: String(id) }),
		});
		return { data: json };
	},
};
